import { toNumberOrUndefined } from '@/utils/area';

// 获取当前日期，格式为 xxxx年xx月xx日
const getCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}年${month}月${day}日`;
};

/**
 * 解析接口或表单中的数字（含千分位逗号、字符串），得到有限数字；无法解析则返回 undefined。
 * 在 {@link toNumberOrUndefined} 基础上去掉千分位，便于与 {@link formatPrice} 成对使用。
 */
export function parseNumericLike(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'number') return toNumberOrUndefined(value);
  const s = String(value).replace(/,/g, '').trim();
  return toNumberOrUndefined(s);
}

/**
 * 格式化价格，添加千分位分隔符
 * 使用 Intl.NumberFormat 作为底层实现，更标准和可靠
 * @param value 价格值（可以是数字、字符串或null/undefined）
 * @param decimals 保留小数位数，默认为0（不保留小数）。如果传入-1，则智能显示（整数不显示小数，有小数则显示最多2位）
 * @returns 格式化后的价格字符串，如果值为空则返回 '-'
 */
const formatPrice = (value: number | string | null | undefined, decimals: number = 0): string => {
  if (value === null || value === undefined || value === '' || value === '-') {
    return '-';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  
  if (isNaN(numValue) || numValue === 0) {
    return '-';
  }
  
  try {
    // 智能模式：整数不显示小数，有小数则显示最多2位
    if (decimals === -1) {
      const isInteger = Number.isInteger(numValue);
      if (isInteger) {
        return new Intl.NumberFormat('zh-CN').format(numValue);
      } else {
        return new Intl.NumberFormat('zh-CN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(numValue);
      }
    }
    
    // 使用 Intl.NumberFormat 进行千分位格式化
    if (decimals > 0) {
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue);
    }
    
    return new Intl.NumberFormat('zh-CN').format(numValue);
  } catch {
    // 降级处理：如果 Intl.NumberFormat 不可用，使用 toLocaleString
    return numValue.toLocaleString('zh-CN');
  }
};

export { getCurrentDate, formatPrice };
